import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { AlertCircle } from 'react-feather';
import { isInstallmentPaymentLocked } from '../../../util/installmentLock';

/**
 * InstallmentPaymentFailedBanner
 *
 * Non-dismissable red banner shown when a Lifetime-installments coach's
 * most recent installment charge failed. Unlike every other plan on the
 * platform (where a failed charge stays silent through Chargebee's own
 * dunning window — see subscription.js's handlePaymentEvent), this plan
 * locks tier/premium access IMMEDIATELY on the first payment_failed event
 * (see installmentLifetime.js's file-level comment for why). This banner
 * is the coach's only signal that happened and what to do about it.
 *
 * The Chargebee subscription itself is never cancelled on a failed
 * installment — "Update payment method" below sends the coach to the same
 * billing page every other plan already uses (/account/payments/pricing),
 * where the Chargebee customer portal lets them fix their card. That
 * retries the SAME stuck invoice; no new checkout, no risk of being
 * charged for an installment they already paid.
 *
 * Mutually exclusive with SubscribeToBeVisibleBanner/Popup — both of those
 * are guarded (via isInstallmentPaymentLocked) to stay silent while this is
 * showing, since their generic "start a free trial" copy would be wrong
 * for a coach who already paid part of this plan.
 */
const InstallmentPaymentFailedBanner = ({ currentUser, history }) => {
  const isProvider = currentUser?.attributes?.profile?.publicData?.isProvider;
  if (!currentUser || !isProvider) return null;
  if (!isInstallmentPaymentLocked(currentUser)) return null;

  const subscriptionDetails = currentUser.attributes.profile.metadata.subscriptionDetails;
  const installmentsPaid =
    typeof subscriptionDetails.installmentsPaid === 'number' ? subscriptionDetails.installmentsPaid : 0;
  const installmentsTotal =
    typeof subscriptionDetails.installmentsTotal === 'number' ? subscriptionDetails.installmentsTotal : 3;
  const failedInstallmentNumber = Math.min(installmentsPaid + 1, installmentsTotal);

  const goToBilling = () => history.push('/account/payments/pricing');

  return (
    <div className="p-4 mb-5 bg-red-50 border border-red-300 rounded-md flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <AlertCircle className="text-red-600 flex-shrink-0" size={24} />
        <div>
          <p className="m-0 font-semibold text-red-900 text-base">
            Your installment {failedInstallmentNumber} of {installmentsTotal} payment failed
          </p>
          <p className="m-0 text-red-800 text-sm mt-1">
            We couldn't charge your card. Your dashboard and listings are paused until it's fixed — update your
            payment method and we'll automatically retry the same charge. You already paid {installmentsPaid} of{' '}
            {installmentsTotal} installments, so you won't be charged for those again.
          </p>
        </div>
      </div>
      <button
        onClick={goToBilling}
        className="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-md text-sm flex-shrink-0"
      >
        Update payment method
      </button>
    </div>
  );
};

const mapStateToProps = state => ({
  currentUser: state.user.currentUser,
});

export default withRouter(connect(mapStateToProps)(InstallmentPaymentFailedBanner));
